import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getDisplayPerPage, getIsDirty, getLang, setIsDirty } from "store/userSlice";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { api } from "helpers/axios";
import PreviewModal from "components/BalanceScorecard/PreviewModal";
import { fetchFields, getBalancedScorecardFields, getCalculationRelatedFields, getConditionalFields, getFixedFields, getFreeFields, getSystemFields } from "store/fieldsSlice";
import { emptyHtml, errorMessage, formatNumberTo, leaveConfirm, idleCloseConfirm, idleClosedInfo, removeTrailingZeros, scrollToTop } from "helpers/common";
import * as Sentry from "@sentry/react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { fetchInterviewsSettings, getInterviewsSettings } from "store/interviewsSettingsSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import useWindowDimensions from "hooks/useWindowDimensions";
import Content from "components/BalanceScorecard/Content";
import { useIdleTimer } from "react-idle-timer";
import EmployeeTableColumn from "components/EmployeeManagement/EmployeeTableColumn";
import { tableResize } from "helpers/tableResize";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { getStateEmployeeSettings } from "store/stateEmployeeSlice";
import EditorInput from "components/ui/EditorInput";
import { ReactComponent as AddCommentIcon } from 'assets/images/icons/add-comment.svg';
import { ReactComponent as EditCommentIcon } from 'assets/images/icons/edit-comment.svg';
import { toast } from "helpers/toast";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { en, de } from 'yup-locales';
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { getGeneralSettings } from "store/generalSettingsSlice";
import { fetchRegularEvaluationLevels, getRegularEvaluationLevels } from "store/balanceScorecardSlice";
import SettingName from "components/BalanceScorecard/Custom/DB/SettingName";
import { Button, Input, InputAffix, NativeSelect } from "components/ui";

function BatchConfirmItemComment({ setShowComment, data, handleSave, readOnlyMode }) {
  const { editableTranslate } = useTranslation();

  const [comment, setComment] = useState(data?.comment || null);

  const [commentError, setCommentError] = useState(false);

  const handleSaveComment = () => {
    if (comment && !commentError) {
      handleSave(comment);
      setShowComment(false);
    }
  };

  return <div className="v2-level-comment-div position-relative">
    <EditorInput data-testid="comment-input"
      className={`${readOnlyMode ? 'hide-toolbar' : ''}`}
      value={comment}
      onChangeCallback={(v) => setComment(v)}
      validate={(v) => v ? setCommentError(true) : setCommentError(false)}
      readOnly={readOnlyMode} />
    {
      !readOnlyMode && <Button onClick={handleSaveComment} variant="chip-success" icon="bi-check" className="btn-save-level-comment" data-testid="confirm-button" />
    }
    <Button onClick={() => setShowComment(false)} variant="chip-accent" icon="bi-x" className="btn-cancel-level-comment" data-testid="cancel-button" />
  </div>;
}

function BatchConfirmItem({ index, watchItems, setValue }) {
  const { editableTranslate } = useTranslation();

  const [showComment, setShowComment] = useState(false);

  const item = useMemo(() => {
    return watchItems[index];
  }, [index, watchItems]);

  const balancedScorecard = useMemo(() => {
    return item?.original;
  }, [item]);

  const levels = useMemo(() => {
    if (!balancedScorecard?.balanced_scorecard_setting?.settings?.systematicObjectivePerformanceLevelGrid) return [];

    return balancedScorecard?.balanced_scorecard_setting?.settings?.systematicObjectivePerformanceLevelGrid?.map(item => {
      const temp = {};
      for (const k in item) {
        temp[k.replace('systematicObjective', '').toLocaleLowerCase()] = item[k];
      }
      return temp;
    });
  }, [balancedScorecard]);

  const isMandatory = useMemo(() => {
    const lvl = item?.level;
    const firstLvl = balancedScorecard?.agreement_data?.overall_result?.first_evaluator?.level;
    const settings = balancedScorecard?.balanced_scorecard_setting?.settings;
    const extremeLevels = levels.filter(l => l.edgevalue).map(l => l.level);

    return settings?.systematicMandatoryCommentForExtremeRating === true
      && settings?.systematicMandatoryCommentForExtremeRatingType === 'based_on_single_objective'
      && (
        (lvl && extremeLevels.includes(lvl))
        || (lvl && lvl !== firstLvl)); // LIX-1268
  }, [levels, item]);

  const labeledIndividualResult = useMemo(() => {
    let tempTotalValue = balancedScorecard?.agreement_data?.totalValue?.systematic || 0;
    if (typeof tempTotalValue === 'string') tempTotalValue = parseFloat(formatNumberTo(tempTotalValue, '.'));

    for (let i = levels.length - 1; i >= 0; i--) {
      const lvl = levels[i]?.level;
      const val = parseFloat(formatNumberTo(levels[i]?.value, '.'));
      if (val % 1 === 0.5) {
        if (tempTotalValue === val) return lvl;
      } else if (Math.abs(val - tempTotalValue) < 0.5) return lvl;
    }

    return '-';
  }, [levels]);

  useEffect(() => {
    if (isMandatory !== undefined) {
      setValue(`items.${index}.mandatory`, isMandatory);
    }
  }, [isMandatory]);

  const handleLevelChange = (e) => {
    setValue(`items.${index}.level`, e.target.value);
  };

  const handleSaveComment = (val) => {
    setValue(`items.${index}.comment`, val);
  };

  const handleCheckboxChange = (targetChecked) => {
    setValue(`items.${index}.checked`, targetChecked);
  };

  return <div>
    <div className="form-check">
      <label className="form-check-label w-100">
        <input className={`form-check-input`} type="checkbox" checked={item?.checked || false} onChange={e => handleCheckboxChange(e.target.checked)} data-testid={`batch-confirm-item-${index}`} />
        <span className="d-flex align-items-start justify-content-between ml-1">
          <div>
            {balancedScorecard?.employee?.name}<br />
            <small className="text-muted">{balancedScorecard?.employee?.employee_number}</small>
          </div>
          <span className="badge badge-pill badge-light font-weight-normal">{balancedScorecard?.balanced_scorecard_setting?.name}</span>
        </span>
      </label>
    </div>
    <div className="d-flex mt-1">
      <input type="checkbox" className="invisible mr-1" data-testid={`batch-confirm-item-${index}-spacer`} />
      <div className="w-100">
        <table className="item-table w-100">
          <tbody>
            <tr className="tr-no-border-bottom">
              <td className="pt-1 text-center">{editableTranslate('state_employee.batch_confirm_result_individual')}</td>
              <td className="pt-1 no-border-left-right text-center">{editableTranslate('state_employee.batch_confirm_initial_evaluator_result')}</td>
              <td className="pt-1 text-center">{editableTranslate('state_employee.batch_confirm_secondary_evaluator_result')}</td>
            </tr>
            <tr className="tr-no-border-top">
              <td className="pt-1 pb-2 text-center">
                <span>{labeledIndividualResult}</span>
              </td>
              <td className="no-border-left-right pt-1 pb-2 text-center">
                <span>{balancedScorecard?.agreement_data?.overall_result?.first_evaluator?.level}</span>
              </td>
              <td className={`pt-1 pb-2 ${showComment ? 'no-border-bottom' : ''}`}>
                <div className="d-flex align-items-center justify-content-center">
                  <NativeSelect data-testid="level-select" className="w-auto mr-2" value={item?.level} onChange={e => handleLevelChange(e)} disabled={item?.checked}>
                    {
                      levels.filter(l => l?.description !== 'sub').map((lvl, i) => <option key={i} value={lvl.level}>{lvl.level}</option>)
                    }
                  </NativeSelect>
                  {
                    isMandatory && emptyHtml(item?.comment) ? <Button variant="ghost" size="inline" icon="bi-exclamation-circle-fill" data-tooltip-id="extreme-comment-tooltip" onClick={() => setShowComment(!showComment)} data-testid="comment-toggle" /> :
                      <>
                        {
                          emptyHtml(item?.comment) ? <AddCommentIcon onClick={() => setShowComment(!showComment)} className="comment-icon cursor-pointer" data-tooltip-id="leave-comment-tooltip" />
                            : <EditCommentIcon onClick={() => setShowComment(!showComment)} className="comment-icon cursor-pointer" data-tooltip-id="leave-comment-tooltip" />
                        }
                      </>
                  }
                </div>
              </td>
            </tr>
            {
              showComment && <tr>
                <td colSpan={3} className="p-2 no-border-top">
                  <BatchConfirmItemComment setShowComment={setShowComment} data={item} handleSave={handleSaveComment} readOnlyMode={item?.checked} />
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
}

function BatchConfirmGroup({ index, totalGroups, group, balancedScorecards, confirmList, handleCheck }) {
  const lang = useSelector(getLang);

  const [show, setShow] = useState(true);

  const checkedAll = useMemo(() => {
    let count = 0;
    balancedScorecards.forEach(bs => {
      const exist = confirmList.find(cl => cl.hash_id === bs.hash_id);
      if (exist) count++;
    });
    return count === balancedScorecards.length;
  }, [balancedScorecards, confirmList]);

  const schema = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        hash_id: yup.string().required(),
        level: yup.string().required(),
        comment: yup.string().notRequired(),
        mandatory: yup.boolean().notRequired(),
        checked: yup.boolean().notRequired(),
        original: yup.mixed()
      })
    )
  });

  const { control, setValue, reset } = useForm({
    resolver: yupResolver(schema)
  });

  const { fields } = useFieldArray({
    control,
    name: "items"
  });

  const watchItems = useWatch({
    control,
    name: 'items'
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    const arr = balancedScorecards?.map(bs => ({
      hash_id: bs.hash_id,
      level: bs?.agreement_data?.overall_result?.first_evaluator?.level,
      comment: null,
      mandatory: false,
      checked: false,
      original: bs
    }));

    reset({
      items: arr
    });
  }, [balancedScorecards]);

  useEffect(() => {
    if (watchItems) {
      watchItems.forEach(item => {
        handleCheck(item, item.checked);
      });
    }
  }, [watchItems]);

  const handleToggle = (e) => {
    if (['INPUT', 'LABEL', 'SPAN'].includes(e.target.tagName)) return;
    setShow(!show);
  };

  const handleCheckedAll = (checked) => {
    for (let i = 0; i < watchItems.length; i++) {
      setValue(`items.${i}.checked`, checked);
    }
  };

  return <div className={`${'col-lg-6'}`}>
    <div className="secondary-evaluator-batch-confirm-group">
      <div data-testid={`batch-confirm-group-toggle-${group?.name}`} className="group-label-div p-2" onClick={handleToggle}>
        <div className="form-check">
          <label className="form-check-label">
            <input className={`form-check-input`} type="checkbox" checked={checkedAll} onChange={e => handleCheckedAll(e.target.checked)} data-testid={`batch-confirm-group-all-${group?.name}`} />
            <span className="font-weight-bold ml-1">{group?.name}</span>
          </label>
        </div>
        <i className={`bi ${show ? 'bi-chevron-up' : 'bi-chevron-down'} cursor-pointer`}></i>
      </div>
      {
        show && <div className="p-2">
          {
            fields.map((field, i) => <Fragment key={field.id}>
              <BatchConfirmItem index={i} watchItems={watchItems} setValue={setValue} />

              {i < balancedScorecards.length - 1 && <hr className="my-3" />}
            </Fragment>)
          }
        </div>
      }
    </div>
  </div>;
}

function BatchConfirmMode({ balancedScorecards, toggleBatchConfirmMode, fetchBalancedScorecards }) {
  const { translate, editableTranslate } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [confirmList, setConfirmList] = useState([]);

  const groupedBalancedScorecards = useMemo(() => {
    const arr = [];

    if (balancedScorecards) {
      const filtered = balancedScorecards.filter(bs => bs.initial_evaluation_completed_at && !bs.evaluation_completed_at);

      for (let i = 0; i < filtered.length; i++) {
        let item = filtered[i];
        let groupFound = false;

        for (let j = 0; j < arr.length; j++) {
          if (arr[j]?.group?.hash_id === item?.group?.hash_id) {
            arr[j].items.push(item);
            groupFound = true;
            break;
          }
        }

        if (!groupFound) {
          arr.push({
            group: item.group,
            items: [item]
          });
        }
      }
    }

    return arr;
  }, [balancedScorecards]);

  const handleCheck = (item, checked) => {
    setConfirmList((prev) => {
      const arr = [...prev];

      const foundIndex = arr.findIndex((a) => a?.hash_id === item?.hash_id);

      if (checked) {
        if (foundIndex > -1) {
          arr[foundIndex] = { ...item };
        } else {
          arr.push({ ...item });
        }
      } else {
        if (foundIndex > -1) {
          arr.splice(foundIndex, 1);
        }
      }

      return arr;
    });
  };

  const validateAll = () => {
    let err = null;

    for (const item of confirmList) {
      if (!item?.level) {
        err = translate('evaluation.validation.evaluation_level');
        break;
      } else if (item?.mandatory && emptyHtml(item?.comment)) {
        err = translate('state_employee.second_evaluator_level_comment');
        break;
      }
    }

    if (err) {
      setError(err);
      return false;
    }
    return true;
  };

  const handleConfirm = async (e) => {
    e?.currentTarget?.blur();

    if (!validateAll()) return;

    setError(null);
    setLoading(true);

    const formData = {
      balanced_scorecards: confirmList.map(cl => ({
        hash_id: cl.hash_id,
        level: cl.level,
        comment: cl.comment
      }))
    };

    try {
      const { status } = await api.post(`/employee-balanced-scorecards/batch_confirm/secondary_evaluator`, formData);
      if (status === 200) {
        fetchBalancedScorecards(false);
        setConfirmList([]);

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setError(errorMessage(err, 'save balanced scorecards batch confirm secondary evaluator'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  return <div>
    <div className="row">
      <div className="col-auto align-self-center">
        <h4 className="mb-0">{editableTranslate('state_employee.batch_confirm_secondary_evaluator_title')}</h4>
      </div>
      <div className="col">
        <div className="d-flex justify-content-end">
          <Button onClick={(e) => toggleBatchConfirmMode(e, false)} variant="secondary" data-testid="cancel-button">
            {editableTranslate('button.cancel')}
          </Button>
          <Button onClick={(e) => handleConfirm(e)} variant="primary" className="ml-2" loading={loading} disabled={confirmList.length === 0} data-testid="confirm-button">
            {!loading && <i className="bi bi-check-lg mr-1"></i>}
            {editableTranslate('state_employee.batch_confirm_secondary_button')} <span>({confirmList.length})</span>
          </Button>
        </div>
      </div>
    </div>
    {
      error && <div className="mt-3">
        <ErrorAlert error={error} onClose={() => setError(null)} />
      </div>
    }
    <div className="row mt-3">
      {
        groupedBalancedScorecards.map((item, i) => <BatchConfirmGroup key={item?.group?.hash_id} index={i} totalGroups={groupedBalancedScorecards.length} group={item.group} balancedScorecards={item.items} confirmList={confirmList} handleCheck={handleCheck} />)
      }
      {
        groupedBalancedScorecards.length === 0 && <div className="col">
          <div className="text-center" data-testid="empty-state">
            {editableTranslate('global.no_data')}
          </div>
        </div>
      }
    </div>

    <ReactTooltip id="leave-comment-tooltip" place="bottom" content={translate('tooltips.balanced_scorecard.leave_comment_tooltip')} />
    <ReactTooltip id="extreme-comment-tooltip" place="bottom" content={translate('tooltips.balanced_scorecard.extreme_comment_tooltip')} />
  </div>;
}

function EvaluationStats({ id = null, data = {}, hasError = false, centered = true, thirdCol = true, displayLineAfter = false }) {
  const { translate, editableTranslate } = useTranslation();

  const cleanedData = useMemo(() => {
    const newData = structuredClone(data);

    return newData;
  }, [data]);

  const getOpacityClass = (i, total) => {
    const middle = Math.floor(total / 2);

    if (total % 2 === 1 && middle === i) return '';

    const distance = Math.abs(middle - i);

    if (distance === 2) return 'opacity-25';
    if (distance === 1) return 'opacity-50';
  };

  return <div className={`bg-white w-100 ${hasError ? 'my-4' : 'mb-4'}`}>
    <div className={`row ${centered ? 'justify-content-center' : ''}`}>
      <div className="col-4 max-w-500">
        <div className="simple-stats-tile">
          <div className="gray font-weight-bold mb-3">{editableTranslate('state_employee.secondary_tiles.progress', 'FORTSCHRITT')}</div>

          <div className="d-flex align-items-center">
            <div className="w-100">
              {
                !cleanedData?.hasOwnProperty('tile1') ? <div className="text-center" data-testid="loading">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="sr-only">Wird geladen...</span>
                  </div>
                </div> : <>
                  <div>
                    <div className="multiple-value-bar no-separator">
                      <div style={{ 'width': (cleanedData?.tile1?.secondary?.ratio || 0) + '%' }}
                        data-tooltip-id={(id ? id + '-' : '') + `stats-evaluated-tile-1-secondary-tooltip`}
                        data-tooltip-content={cleanedData?.tile1?.secondary?.evaluated + ' ' + translate('state_employee.secondary_tiles.completed', 'abgeschlossen')}></div>

                      <div className="opacity-50" style={{ 'width': (cleanedData?.tile1?.secondary?.pending?.ratio || 0) + '%' }}
                        data-tooltip-id={(id ? id + '-' : '') + `stats-evaluated-tile-1-secondary-tooltip`}
                        data-tooltip-content={cleanedData?.tile1?.secondary?.pending?.value + ' ' + translate('state_employee.secondary_tiles.open', 'offen')}></div>

                      <div className="diagonal-lines" style={{ 'width': (cleanedData?.tile1?.secondary?.pending_initial?.ratio || 0) + '%' }}
                        data-tooltip-id={(id ? id + '-' : '') + `stats-evaluated-tile-1-secondary-tooltip`}
                        data-tooltip-content={cleanedData?.tile1?.secondary?.pending_initial?.value + ' ' + translate('state_employee.secondary_tiles.not_yet_open', 'noch nicht freigegeben')}></div>
                    </div>
                    <div className="multiple-value-bar-label-normal mt-1">
                      <div className="gray text-smaller mr-3">
                        <span className="indicator v-middle bg-accent"></span>
                        <span className="v-middle ml-1">{cleanedData?.tile1?.secondary?.evaluated}</span>
                        <span className="v-middle ml-1">{editableTranslate('state_employee.secondary_tiles.completed', 'abgeschlossen')}</span>
                      </div>
                      <div className="gray text-smaller mr-3">
                        <span className="indicator v-middle bg-accent opacity-50"></span>
                        <span className="v-middle ml-1">{cleanedData?.tile1?.secondary?.pending?.value}</span>
                        <span className="v-middle ml-1">{editableTranslate('state_employee.secondary_tiles.open', 'offen')}</span>
                      </div>
                      <div className="gray text-smaller">
                        <span className="indicator v-middle diagonal-lines bordered"></span>
                        <span className="v-middle ml-1">{cleanedData?.tile1?.secondary?.pending_initial?.value}</span>
                        <span className="v-middle ml-1">{editableTranslate('state_employee.secondary_tiles.not_yet_open', 'noch nicht freigegeben')}</span>
                      </div>
                    </div>

                    <ReactTooltip id={(id ? id + '-' : '') + `stats-evaluated-tile-1-secondary-tooltip`} place="bottom" />
                  </div>
                </>
              }
            </div>
          </div>
        </div>
      </div>

      <div className="col-4 max-w-500">
        <div className="simple-stats-tile">
          <div className="gray font-weight-bold mb-3">{editableTranslate('state_employee.secondary_tiles.distribution_of_initial_evaluations', 'VERTEILUNG DER ERSTBEURTEILUNGEN')}</div>

          {
            !cleanedData?.hasOwnProperty('tile2') ? <div className="w-100 text-center" data-testid="loading">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="sr-only">Wird geladen...</span>
              </div>
            </div> : <>
              {
                (!cleanedData?.tile2?.distributions || cleanedData?.tile2?.distributions?.length === 0 || cleanedData?.tile1?.initial?.evaluated === 0) && <>
                  <div className="single-value-bar">
                    <div style={{ 'width': '0%' }}></div>
                  </div>
                </>
              }
              {
                (cleanedData?.tile2?.distributions?.length > 0 && cleanedData?.tile1?.initial?.evaluated > 0) && <div className="multiple-value-bar no-separator">
                  {
                    cleanedData?.tile2?.distributions?.map((dist, i) => <div key={i}
                      className={getOpacityClass(i, cleanedData?.tile2?.distributions?.length)}
                      style={{ 'width': (dist?.ratio > 0 ? dist?.ratio + '%' : '8px') }}
                      data-tooltip-id={(id ? id + '-' : '') + `stats-evaluated-tile-2-distribution-tooltip`}
                      data-tooltip-content={`${dist?.label} (${dist?.ratio} %)`}>
                    </div>)
                  }
                </div>
              }
              <ReactTooltip id={(id ? id + '-' : '') + `stats-evaluated-tile-2-distribution-tooltip`} place="bottom" />
              {
                cleanedData?.tile2?.distributions?.length > 0 &&
                <div className="multiple-value-bar-label-normal mt-1">
                  {
                    cleanedData?.tile2?.distributions?.map((dist, i) => <div key={i}
                      className={`gray text-mid text-uppercase font-weight-bold ${i < cleanedData?.tile2?.distributions?.length ? 'mr-3' : ''}`}>
                      <span className={`indicator v-middle ` + getOpacityClass(i, cleanedData?.tile2?.distributions?.length)}></span>
                      <span className="v-middle ml-1">{dist?.label}</span>
                      <span className="v-middle ml-1 font-weight-normal">({removeTrailingZeros(+dist?.ratio, '.')} %)</span>
                    </div>)
                  }
                </div>
              }
            </>
          }
        </div>
      </div>

      {
        thirdCol && <div className="col-4 max-w-500">
          <div className="simple-stats-tile">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="gray font-weight-bold">{editableTranslate('state_employee.secondary_tiles.assessment_criteria', 'BEWERTUNGSVORGABE')}</div>
              {
                cleanedData?.tile2?.assessment?.ratio >= cleanedData?.tile2?.assessment?.threshold && <div>
                  <i className="bi bi-check-circle-fill v-middle"></i>
                  <span className="green-color v-middle ml-1">{editableTranslate('state_employee.secondary_tiles.fulfilled', 'erfüllt')}</span>
                </div>
              }
            </div>

            {
              (cleanedData?.tile2?.hasOwnProperty('assessment') && cleanedData?.tile2?.assessment) ? <>
                <div>
                  <div className="threshold-bar"
                    data-tooltip-id={cleanedData?.tile2?.assessment?.ratio <= 0 ? (id ? id + '-' : '') + `stats-evaluated-tile-2-assessment-tooltip` : null}>
                    <div className="fill"
                      data-tooltip-id={cleanedData?.tile2?.assessment?.ratio > 0 ? (id ? id + '-' : '') + `stats-evaluated-tile-2-assessment-tooltip` : null}
                      style={{ width: cleanedData?.tile2?.assessment?.ratio + '%' }}></div>
                    <div className="marker" style={{ left: cleanedData?.tile2?.assessment?.threshold + '%' }}></div>
                  </div>

                  <div className="d-flex text-smaller gray justify-content-between mt-1">
                    <span>0 %</span>
                    <span className="font-weight-600">{editableTranslate('state_employee.secondary_tiles.specification', 'Vorgabe')} {cleanedData?.tile2?.assessment?.threshold + ' %'}</span>
                    <span>100 %</span>
                  </div>
                </div>

                <ReactTooltip id={(id ? id + '-' : '') + `stats-evaluated-tile-2-assessment-tooltip`} content={`${removeTrailingZeros(+cleanedData?.tile2?.assessment?.ratio, '.')} % ` + translate('state_employee.secondary_tiles.current', 'aktuell')} place={cleanedData?.tile2?.assessment?.ratio <= 0 ? 'left' : 'bottom'} />
              </> : <div className="w-100 text-center" data-testid="loading">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="sr-only">Wird geladen...</span>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>

    {
      displayLineAfter && <div className={`row ${centered ? 'justify-content-center' : ''}`}>
        <div className={`${thirdCol ? 'col-12 max-w-1500' : 'col-8 max-w-1000'} mt-3`}>
          <hr className="mb-1" />
        </div>
      </div>
    }
  </div>;
}

function EvaluatorRow({ fields, employee, balancedScorecard, setShowPreview, interviews, selectedRow, resizedCount = null }) {
  return <>
    <tr className="tr-separator"></tr>
    <tr data-testid={`row-${balancedScorecard?.hash_id}`} className={`table-row ${selectedRow ? 'selected-row' : ''}`} onClick={() => balancedScorecard.exclude_from_payout ? null : setShowPreview(employee, balancedScorecard)} data-tooltip-id={employee.exclude_from_payout ? `employee-exclusion-tooltip` : (balancedScorecard.exclude_from_payout ? `balanced-scorecard-exclusion-tooltip` : '')}>
      {
        fields.map((field, i) => {
          return <EmployeeTableColumn key={i} field={field} employee={employee} displayLkTag={true} balancedScorecards={[balancedScorecard]} useInterviewIcon={true} resizedCount={resizedCount} />
        })
      }
    </tr>
  </>
}

export default function SecondaryEvaluatorOverview({ headerTranslation = 'state_employee.secondary_evaluation', reviewerView = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const { translate, editableTranslate } = useTranslation();

  const { width } = useWindowDimensions();

  const generalSettings = useSelector(getGeneralSettings);

  const regularEvaluationLevels = useSelector(getRegularEvaluationLevels);

  const groups = useSelector(getGroups);
  const displayPerPage = useSelector(getDisplayPerPage);

  const [error, setError] = useState(null);
  const [lockError, setLockError] = useState(null);

  const [sortCols, setSortCols] = useState([['last_name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const [balancedScorecards, setBalancedScorecards] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || ''); // term sent to the server

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedBalancedScorecard, setSelectedBalancedScorecard] = useState(null);

  const [fixMode, setFixMode] = useState(false);
  const [fixData, setFixData] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);

  const globalIsDirty = useSelector(getIsDirty);

  const [batchConfirmMode, setBatchConfirmMode] = useState(false);

  const [contentDirty, setContentDirty] = useState(false);

  const [tableFields, setTableFields] = useState([]);

  const fixedFields = useSelector(getFixedFields);
  const calculationRelatedFields = useSelector(getCalculationRelatedFields);
  const conditionalFields = useSelector(getConditionalFields);
  const systemFields = useSelector(getSystemFields);
  const freeFields = useSelector(getFreeFields);
  const balancedScorecardFields = useSelector(getBalancedScorecardFields);

  const stateEmployeeSettings = useSelector(getStateEmployeeSettings);

  const interviews = useSelector(getInterviewsSettings);

  const isFullHD = useMemo(() => width >= 1536, [width]);

  const inlineMode = useMemo(() => isFullHD && selectedBalancedScorecard, [isFullHD, selectedBalancedScorecard]);

  const filteredFixedFields = useMemo(() => {
    if (!fixedFields) return [];
    else if (inlineMode) return fixedFields.filter(f => ['first_name', 'last_name'].includes(f.slug));
    return fixedFields.filter(f => f.is_active);
  }, [fixedFields, inlineMode]);

  const filteredCalculationRelatedFields = useMemo(() => {
    if (!calculationRelatedFields || (inlineMode)) return [];
    return calculationRelatedFields.filter(f => f.is_active);
  }, [calculationRelatedFields, inlineMode]);

  const filteredConditionalFields = useMemo(() => {
    if (!conditionalFields || (inlineMode)) return [];
    return conditionalFields.filter(f => f.is_active);
  }, [conditionalFields, inlineMode]);

  const filteredSystemFields = useMemo(() => {
    if (!systemFields || (inlineMode)) return [];
    return systemFields.filter(f => f.is_active);
  }, [systemFields, inlineMode]);

  const filteredFreeFields = useMemo(() => {
    if (!freeFields || (inlineMode)) return [];
    return freeFields.filter(f => f.is_active);
  }, [freeFields, inlineMode]);

  const filteredBalancedScorecardFields = useMemo(() => {
    if (!balancedScorecardFields) return [];
    else {
      let arr = [];
      if (inlineMode) arr = balancedScorecardFields.filter(f => ['lk', 'result', 'trend', 'personal_interviews'].includes(f.slug) && f.is_active);
      else arr = balancedScorecardFields.filter(f => f.is_active);

      return arr.map(f => {
        return { ...f, key: f.translation_key.replace('field.', '') };
      });
    }
  }, [balancedScorecardFields, inlineMode]);

  const resultAsNumeric = useMemo(() => {
    const setting = generalSettings?.find(s => s.key === 'balanced_scorecard_result_format');
    if (setting && setting.value === 'numeric') {
      return true;
    }
    return false
  }, [generalSettings]);

  const statsData = useMemo(() => {
    if (!balancedScorecards) return {};

    let total = balancedScorecards.length;
    let initialEvaluated = 0;
    let secondaryEvaluated = 0;

    let levels = regularEvaluationLevels.filter(l => l?.description !== 'sub').map(l => ({ count: 0, ...l }));

    for (const bs of balancedScorecards) {
      if (bs?.initial_evaluation_completed_at) {
        initialEvaluated++;

        const result = bs?.agreement_data?.overall_result?.first_evaluator?.value || null;

        for (const lvl of levels) {
          if (result && +result === +lvl?.value) {
            lvl.count++;
            break;
          }
        }
      }

      if (bs?.evaluation_completed_at) {
        secondaryEvaluated++;
      }
    }

    // const filteredLevels = levels.filter(l => l.count > 0);

    const assessmentCriteria = stateEmployeeSettings?.state_employee_assessment_criteria || null;

    let assessmentCriteriaRatio = null;

    let tempTotal = 0;

    const distributions = levels.map((lvl, index) => {
      let ratio = Math.round(lvl?.count / initialEvaluated * 100 * 10) / 10;

      if (index === levels.length - 1) {
        ratio = 100 - tempTotal;
      } else {
        tempTotal += ratio;
      }

      ratio = ratio ? ratio.toFixed(1) : 0;

      if (assessmentCriteria && +lvl.value === +assessmentCriteria?.value) {
        assessmentCriteriaRatio = ratio;
      }

      return {
        label: resultAsNumeric ? lvl?.value : lvl?.level,
        count: lvl?.count,
        ratio
      };
    });

    const pendingInitial = (total - initialEvaluated);
    const pendingSecondary = (initialEvaluated - secondaryEvaluated);

    const evaluatedSecondaryRatio = Math.round(secondaryEvaluated / total * 100 * 10) / 10;
    const pendingSecondaryRatio = Math.round(pendingSecondary / total * 100 * 10) / 10;
    const pendingInitialRatio = 100 - evaluatedSecondaryRatio - pendingSecondaryRatio;

    return {
      tile1: {
        initial: {
          evaluated: initialEvaluated,
          total,
          ratio: (Math.round(initialEvaluated / total * 100 * 10) / 10).toFixed(1)
        },
        secondary: {
          evaluated: secondaryEvaluated,
          total: initialEvaluated,
          ratio: evaluatedSecondaryRatio.toFixed(1),
          pending: {
            value: pendingSecondary,
            ratio: pendingSecondaryRatio.toFixed(1)
          },
          pending_initial: {
            value: pendingInitial,
            ratio: pendingInitialRatio.toFixed(1)
          }
        }
      },
      tile2: {
        distributions,
        assessment: assessmentCriteria ? {
          threshold: assessmentCriteria?.threshold,
          ratio: assessmentCriteriaRatio
        } : null
      }
    };
  }, [balancedScorecards, regularEvaluationLevels, resultAsNumeric, stateEmployeeSettings]);

  const settingName = useMemo(() => {
    let employeeName = [selectedEmployee?.last_name, selectedEmployee?.first_name].filter(n => n).join(', ');

    return !selectedEmployee ? <span>{editableTranslate('balanced_scorecard.management')}</span> :
      <>
        <span className="v-middle" data-tooltip-id="employee-number-tooltip">{employeeName + ' - '}</span>
        <SettingName setting={selectedBalancedScorecard?.balanced_scorecard_setting} className="v-middle" />
      </>;
  }, [selectedEmployee, selectedBalancedScorecard, translate]);

  const headerRef = useRef(null);
  const floatingSeparatorRef = useRef(null);

  const theadRef = useRef(null);

  const tableRef = useRef(null);

  const fixDivRef = useRef(null);

  const onIdle = () => {
    if (selectedBalancedScorecard) {
      handleClosePreviewModal(null, true);
    }
  };

  const { start, pause } = useIdleTimer({
    onIdle,
    timeout: (1 * 300 * 1000),
    stopOnIdle: true,
    startManually: true
  });

  useEffect(() => {
    const tf = [...filteredFixedFields, ...filteredCalculationRelatedFields, ...filteredConditionalFields, ...filteredSystemFields, ...filteredFreeFields, ...filteredBalancedScorecardFields];

    const arr = [];
    for (const f of tf) {
      if (f.display_in.includes('rating_overview')) {
        arr.push(f);
      }
    }

    arr.push({
      slug: 'info',
      alignment: 'left',
      name: 'Info',
      translation_key: 'state_employee.info',
      is_balanced_scorecard_specific: false,
      no_sort: true
    });

    setTableFields(arr);
  }, [filteredFixedFields, filteredCalculationRelatedFields, filteredConditionalFields, filteredSystemFields, filteredFreeFields, filteredBalancedScorecardFields]);

  useEffect(() => {
    if (!groups) dispatch(fetchGroups());

    if (!fixedFields || !freeFields || !balancedScorecardFields) dispatch(fetchFields());

    if (!interviews) dispatch(fetchInterviewsSettings());

    if (!regularEvaluationLevels?.length) dispatch(fetchRegularEvaluationLevels());
  }, []);

  useEffect(() => {
    const theadEl = theadRef.current;
    if (!theadEl) return;

    const headerEl = headerRef.current;
    if (!headerEl) return;

    const floatingEl = floatingSeparatorRef.current;
    if (!floatingEl) return;

    const fixDivEl = fixDivRef.current;

    const parentEl = theadEl.parentElement;
    const headerParentEl = headerEl.parentElement;
    const floatingParentEl = floatingEl.parentElement;

    const handleScrollTable = () => {
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const tabsParentCoordinates = headerParentEl.getBoundingClientRect();
      if (tabsParentCoordinates.y < navHeight) {
        headerEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7}px, 0)`;
        theadEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7}px, 0)`;
        if (fixDivEl) fixDivEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7}px, 0)`;

        floatingParentEl.style.display = 'block';
        floatingEl.style.display = 'block';
        floatingEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight}px, 0)`;
        floatingEl.style.height = (headerEl.offsetHeight + theadEl.offsetHeight + (fixDivEl ? fixDivEl.offsetHeight + 14 : 0) + 14 + 14) + 'px';
      } else {
        headerEl.style.transform = 'translate3d(0,0,0)';
        theadEl.style.transform = 'translate3d(0,0,0)';
        if (fixDivEl) fixDivEl.style.transform = 'translate3d(0,0,0)';

        floatingParentEl.style.display = 'none';
        floatingEl.style.display = 'hidden';
        floatingEl.style.transform = 'translate3d(0,0,0)';
        floatingEl.style.height = '.5rem';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [fixData]);

  // useEffect(() => {
  //   if (displayPerPage > 0) {
  //     fetchEmployees(true);
  //   }
  // }, [displayPerPage]);

  useEffect(() => {
    if (searchParams.get('source') === 'calculation') {
      const budgetHashId = searchParams.get('budget_hash_id');
      const budgetName = searchParams.get('budget_name');
      const errorKey = searchParams.get('error_key');

      setFixData({ hash_id: budgetHashId, budget_name: budgetName, error_key: errorKey });
      setFixMode(true);
    } else {
      setFixMode(false);
      setFixData(null);
    }

    setSearchValue(searchParams.get('search') || '');
    setDebouncedSearch(searchParams.get('search') || '');

    if (searchParams.get('mode') === 'batch_confirm') {
      setBatchConfirmMode(true);
    } else {
      setBatchConfirmMode(false);
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(setIsDirty(contentDirty));
  }, [contentDirty]);

  useEffect(() => {
    // if (displayPerPage > 0) {
    if (searchParams.get('source') === 'calculation') {
      if (fixData) { // wait for fixData if coming from calculation
        fetchBalancedScorecards(true);
      }
    } else {
      fetchBalancedScorecards(true);
    }
    // }
  }, [debouncedSearch, sortCols, fixData, searchParams.get('source')]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchValue), 350);
    return () => clearTimeout(t);
  }, [searchValue]);

  useEffect(() => {
    if (!inlineMode && selectedBalancedScorecard && !showPreviewModal) setShowPreviewModal(true);
    else if (inlineMode && selectedBalancedScorecard && showPreviewModal) {
      setShowPreviewModal(false);
      document.body.style.overflowY = 'auto';
    }
  }, [inlineMode]);

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 250,
    email: 300,
    individual_pay_factor: 250,
    individual_average_rating: 300,
    exclude_from_payout: 300,
    balanced_scorecard_template: 400,
    lk: 100,
    info: 50
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'secondary_evaluator_overview_state_employees', false, {}, maxColWidth, setResizedCount);
  }, [balancedScorecards, selectedBalancedScorecard, triggerColWidth, tableFields]);

  useEffect(() => {
    if (!sortPinCol) {
      setTriggerColWidth(p => p + 1);
      setResizedCount(p => p + 1);
    }
  }, [sortCols]);

  useEffect(() => {
    setResizedCount(p => p + 1);
  }, [batchConfirmMode, inlineMode, selectedBalancedScorecard]);

  const fetchSeq = useRef(0); // only the newest fetch may update the list
  const fetchBalancedScorecards = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const seq = ++fetchSeq.current;
    try {
      let url = `/employee-balanced-scorecards/secondary-evaluator`;
      if (debouncedSearch) url += `?search=${encodeURIComponent(debouncedSearch)}`;
      if (fixData) url += (debouncedSearch ? '&' : '?') + `fix_error_key=${fixData?.error_key}&budget_hash_id=${fixData?.hash_id}`;

      if (sortCols && sortCols.length > 0) {
        url += ((debouncedSearch || fixData) ? '&' : '?') + `sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }

      const { data, status } = await api.get(url);
      if (seq !== fetchSeq.current) return; // a newer fetch superseded this one

      if (status === 200) {
        setLoading(false);

        const newData = data.data.map((item) => {
          const information = {};

          item.employee.employeeInformation.data.forEach(info => {
            information[info.key] = info.value;
          });

          // item.employee.information = information;
          item.employee.information = item.employee.information || information;

          return item;
        });

        setBalancedScorecards(newData);
        setError(null);
      }
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      setLoading(false);
      setError(errorMessage(err, 'fetch employees'));
      Sentry.captureException(err);
    }
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field.slug) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field.slug, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const getSortCol = (field) => {
    for (const sc of sortCols) {
      if (sc[0] === field.slug) return sc;
    }
    return null;
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field.slug) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field.slug);
      setSortCols(sortCols.filter(sc => sc[0] === field.slug));
    }
  };

  const handleBtnFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleCancelFilter = () => {
    setShowFilter(false);
  };

  const handleSearchFilter = (e) => {
    e?.preventDefault();
    setSearchParams({
      search: searchValue
    });
  };

  const fetchEmployee = async (hashId = null, balancedScorecard = null) => {
    if (!hashId && !selectedEmployee) return;

    setError(null);
    try {
      const { data, status } = await api.get(`/employees/${hashId || selectedEmployee.hash_id}`);
      if (status === 200) {
        const information = {};
        data.employeeInformation.data.forEach(info => {
          information[info.key] = info.value;
        });
        setSelectedEmployee({ ...data, information });
        if (balancedScorecard) setSelectedBalancedScorecard(balancedScorecard);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch employee'));
      Sentry.captureException(err);
    }
  };

  const postLock = async (hashId) => {
    if (reviewerView) return true;

    setLockError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/lock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('state_employee.lock_forbidden', 'Diese Beurteilung wird gerade von einem anderen Nutzenden bearbeitet: $X.');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setLockError(formatted);
      } else {
        setLockError(errorMessage(err, 'lock employee balanced scorecard'));
      }
    }
  };

  const postUnlock = async (hashId) => {
    if (reviewerView) return true;

    setLockError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/unlock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('state_employee.lock_forbidden', 'Diese Beurteilung wird gerade von einem anderen Nutzenden bearbeitet: $X.');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setLockError(formatted);
      } else {
        setLockError(errorMessage(err, 'unlock employee balanced scorecard'));
      }
    }
  };

  const setShowPreview = async (employee, balancedScorecard) => {
    if (globalIsDirty) {
      const confirmed = await leaveConfirm();
      if (!confirmed) return;
      else dispatch(setIsDirty(false));
    }

    if (selectedBalancedScorecard) { // old/currently opened
      await postUnlock(selectedBalancedScorecard.hash_id);

      setShowPreviewModal(false);
      setSelectedEmployee(null);
      setSelectedBalancedScorecard(null);
      document.body.style.overflowY = 'auto';
    }

    if (await postLock(balancedScorecard.hash_id)) { // new one // check if it's already locked by someone else
      if (!isFullHD) setShowPreviewModal(true);
      fetchEmployee(employee.hash_id, balancedScorecard);
      scrollToTop();
      if (!reviewerView) start();
    } else if (!reviewerView) {
      pause();
    }
  };

  const handleClosePreviewModal = async (e, idle = false) => {
    let notifyIdle = idle;
    if (globalIsDirty) {
      const choice = idle ? await idleCloseConfirm() : ((await leaveConfirm()) ? 'close' : 'continue');
      if (choice === 'continue') {
        if (!reviewerView) start(); // idle timer stops on idle, restart it so the lock is still released later
        return;
      } else dispatch(setIsDirty(false));
      if (choice === 'close') notifyIdle = false; // the user closed it, no need to tell them
    }

    if (!reviewerView) pause();

    await postUnlock(selectedBalancedScorecard.hash_id);

    setShowPreviewModal(false);
    setSelectedEmployee(null);
    setSelectedBalancedScorecard(null);
    document.body.style.overflowY = 'auto';

    if (notifyIdle) idleClosedInfo(globalIsDirty);
  };

  const toggleBatchConfirmMode = (e, val) => {
    e?.currentTarget?.blur();
    if (val) {
      searchParams.set('mode', 'batch_confirm');
    } else {
      searchParams.delete('mode');
    }
    setSearchParams(searchParams);
  };

  const getLabel = (key, fallback, customName = null) => {
    const sKey = key.replaceAll('.', '-');
    const abbreviation = key + '.abbreviation';

    // on mouse over, show the full text in a tooltip using ReactTooltip
    return <>
      <span data-tooltip-id={sKey}>{customName || editableTranslate(abbreviation, translate(key, fallback))}</span>
      {!customName && <ReactTooltip id={sKey} place="bottom" content={editableTranslate(key, fallback)} clickable />}
    </>
  };

  return (
    <>
      <div className="col d-flex">
        <div className={`bg-white w-100 p-4 content-card min-height-after-navbar`}>
          {
            error && <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }

          {
            !batchConfirmMode && <EvaluationStats id="secondary" data={statsData} hasError={error ? true : false} />
          }

          {
            batchConfirmMode ? <BatchConfirmMode balancedScorecards={balancedScorecards} toggleBatchConfirmMode={toggleBatchConfirmMode} fetchBalancedScorecards={fetchBalancedScorecards} />
              : <div>
                <div className={inlineMode ? 'max-w-500' : ''}>
                  <div className="position-relative" style={{ display: 'none' }}>
                    <div ref={floatingSeparatorRef} className="floating-separator"></div>
                  </div>
                  <div ref={headerRef} className="row position-relative z-3">
                    <div className="col-auto align-self-center">
                      <h4 className="mb-0">{editableTranslate(headerTranslation)}</h4>
                    </div>
                    {
                      !fixMode && <div className="col">
                        <div className="d-flex justify-content-end">
                          <Button onClick={(e) => toggleBatchConfirmMode(e, true)} variant="secondary" className="mr-3" data-testid="batch-confirm-button">
                            {editableTranslate('state_employee.batch_confirm_secondary_evaluator')}
                          </Button>
                          <form className="form-inline" onSubmit={handleSearchFilter}>
                            <InputAffix className={searchValue ? 'bg-red-dark-soft' : ''} prefix={<i className="bi bi-search mr-2"></i>}>
                              <Input data-testid="search-input" type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} placeholder={translate('form.search')} autoComplete="false" maxLength={255} />
                            </InputAffix>
                          </form>

                          {/* <div className="btn-group">
                        <Button variant="secondary" className="ml-3" icon="bi-filter" onClick={handleBtnFilter} data-testid="filter-button">{editableTranslate('form.filter')} <i className="bi bi-chevron-down"></i></Button>
                        <div className={`dropdown-menu dropdown-menu-right p-3 filter-dropdown ${showFilter ? 'show' : ''}`}>
                          <div>
                            <label>{editableTranslate('field.group')}</label>
                            <NativeSelect>
                              <option value="-">alle</option>
                              {
                                groups && groups.map((group, i) => {
                                  return <option key={i} value={group.hash_id}>{group.name}</option>
                                })
                              }
                            </NativeSelect>
                          </div>
                          <div className="mt-3">
                            <Button variant="primary" data-testid="apply-button">{editableTranslate('button.apply')}</Button> <Button variant="secondary" onClick={handleCancelFilter} data-testid="cancel-button">{editableTranslate('button.cancel')}</Button>
                          </div>
                        </div>
                      </div> */}
                        </div>
                      </div>
                    }
                  </div>
                </div>

                <div className="mt-3">
                  <div className={inlineMode ? 'evaluator-inline-mode' : ''}>
                    <div className="evaluator-left-div">
                      {
                        lockError && <div>
                          <ErrorAlert error={lockError} isHtml={true} onClose={() => setLockError(null)} />
                        </div>
                      }
                      <div className="table-responsive has-custom-scrollbar">
                        <table ref={tableRef} className="table evaluation-overview-table" data-testid="balanced-scorecards-table">
                          <thead ref={theadRef} className="sticky-top">
                            <tr>
                              {
                                tableFields.map((field, i) => {
                                  const sc = getSortCol(field);

                                  return <th key={i} scope="col" className={`${!field?.no_sort ? 'cursor-pointer' : ''} ${field.alignment ? 'text-' + field.alignment : ''}`}>
                                    <span className="float-right floating-icons">
                                      {
                                        (!field?.no_sort) && <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${sc && sc[0] === field.slug ? (sc[1] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field)} data-testid={`sort-${field.slug}`} />
                                      }
                                      {
                                        (sc && sc[0] === field.slug) && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field)} data-testid={`sort-pin-${field.slug}`} />
                                      }
                                    </span>
                                    <span data-testid={`header-${field.slug}`} className="th-span" onClick={() => field?.no_sort ? null : handleChangeSortCol(field)}>
                                      {getLabel(field.translation_key, field.name, field?.custom_name)}
                                      {field.name === 'Trend' && <>
                                        <i className="bi bi-info-circle ml-1" data-tooltip-id={`th-trend-tooltip`}></i>
                                        <ReactTooltip id={`th-trend-tooltip`} place="bottom"
                                          content={editableTranslate('tooltips.field.trend.tooltip')} clickable />
                                      </>}
                                      <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                      <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                                    </span>
                                    <div className="th-resize-div" data-field={field.slug}></div>
                                  </th>
                                })
                              }
                            </tr>
                          </thead>
                          <tbody>
                            {
                              !loading && Array.isArray(balancedScorecards) && balancedScorecards.map((item, i) => {
                                return <EvaluatorRow key={'row_' + i} fields={tableFields} employee={item.employee} balancedScorecard={item} setShowPreview={setShowPreview} interviews={interviews} selectedRow={selectedBalancedScorecard?.hash_id === item.hash_id} resizedCount={resizedCount} />
                              })
                            }

                            {
                              (loading || !balancedScorecards) && <tr>
                                <td colSpan={40}>
                                  <div className="d-flex justify-content-center" data-testid="loading">
                                    <div className="spinner-border" role="status">
                                      <span className="sr-only">Wird geladen...</span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            }

                            {
                              !loading && Array.isArray(balancedScorecards) && balancedScorecards.length === 0 && <tr>
                                <td colSpan={40} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                      <TableScrollbar tableRef={tableRef} data={balancedScorecards} trigger={resizedCount} />
                    </div>
                    {
                      inlineMode && <div className="evaluator-right-div">
                        <div className="evaluator-header">
                          <h5 className="font-weight-bold">{settingName}</h5>
                          <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={handleClosePreviewModal} data-testid="close-button"><i className="bi bi-x-lg"></i></span>
                        </div>

                        <Content show={selectedBalancedScorecard ? true : false} employee={selectedEmployee} balancedScorecard={selectedBalancedScorecard} fetchBalancedScorecards={fetchBalancedScorecards} reviewerView={reviewerView} setContentDirty={setContentDirty} fetchEmployee={fetchEmployee} />
                      </div>
                    }
                  </div>
                </div>
              </div>
          }
        </div>
      </div>

      {
        !inlineMode && <PreviewModal settingName={settingName} show={showPreviewModal} handleClose={handleClosePreviewModal} employee={selectedEmployee} balancedScorecard={selectedBalancedScorecard} fetchBalancedScorecards={fetchBalancedScorecards} reviewerView={reviewerView} setContentDirty={setContentDirty} />
      }
      <ReactTooltip id={`employee-exclusion-tooltip`} content={translate('tooltips.evaluation.balanced_scorecard.employee_exclude_from_payout_tooltip')} />
      <ReactTooltip id={`balanced-scorecard-exclusion-tooltip`} content={translate('tooltips.evaluation.state_employee.exclude_from_payout_tooltip', 'Die Beurteilung ist von der Leistungsbewertung ausgenommen.')} />

      <ReactTooltip id={`employee-number-tooltip`} place="bottom" clickable={true} content={selectedEmployee?.employee_number} />
      <ReactTooltip id={`setting-name-tooltip`} place="bottom" content={selectedBalancedScorecard?.balanced_scorecard_setting?.name + ` (${selectedBalancedScorecard?.balanced_scorecard_setting?.description})`} />
    </>
  );
}
